/**
 * useChooseAiBuddy — follow / unfollow the AI companion behind an agents-list
 * entry. UserRelation "Agent" is a toggle, so the same call switches a buddy on
 * and off; the cached Robi conversation is dropped when it is switched off.
 */
import { useCallback, useState } from 'react';
import { useProfile } from '../context/ProfileContext';
import { STORAGE_KEYS } from '../constants/storage';
import { storage } from '../platform/storage';
import { resolveAgentUserId } from '../utils/aiBuddyAgent';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import type { ApiError } from '../types/api';
import type { AIBuddy } from '../types/user';

export interface ChooseAiBuddyResult {
  success: boolean;
  message?: string;
}

export interface UseChooseAiBuddyResult {
  /** Agent id of the buddy the member currently follows, if any. */
  currentAgentId: number | null;
  isCurrent: (buddy: AIBuddy) => boolean;
  /** Id of the buddy whose request is in flight. */
  pendingBuddyId: string | null;
  choose: (buddy: AIBuddy) => Promise<ChooseAiBuddyResult>;
}

export const useChooseAiBuddy = (): UseChooseAiBuddyResult => {
  const { profile, userRelation, fetchProfile } = useProfile();
  const [pendingBuddyId, setPendingBuddyId] = useState<string | null>(null);

  const currentAgentId = profile?.followedAiAgent?.agentId ?? null;

  const isCurrent = useCallback(
    (buddy: AIBuddy) =>
      currentAgentId != null && String(currentAgentId) === String(buddy.id),
    [currentAgentId],
  );

  const choose = useCallback(
    async (buddy: AIBuddy): Promise<ChooseAiBuddyResult> => {
      setPendingBuddyId(buddy.id);
      const isUnfollow = isCurrent(buddy);
      try {
        const agentUserId = await resolveAgentUserId(buddy);
        if (!agentUserId) {
          return { success: false };
        }

        await userRelation(agentUserId, 'Agent');
        if (isUnfollow) {
          await storage.removeItem(STORAGE_KEYS.ROBI_CONVERSATION_ID);
        }
        await fetchProfile();
        return { success: true };
      } catch (caught) {
        return { success: false, message: getApiErrorMessage(caught as ApiError) };
      } finally {
        setPendingBuddyId(null);
      }
    },
    [fetchProfile, isCurrent, userRelation],
  );

  return { currentAgentId, isCurrent, pendingBuddyId, choose };
};

export default useChooseAiBuddy;
